/**
 * HabitItem — single habit row with completion toggle, streak counter, and color dot.
 *
 * Layout: [color dot] [habit name + streak] [completion checkbox/toggle]
 * - Color dot: 8px circle using habit.color
 * - Name: truncated, bold if completed
 * - Streak: StreakCounter component showing currentStreak
 * - Toggle: large circular checkbox (44x44px touch target), filled when complete
 * - Completion animation: scale bounce on toggle (300ms)
 *
 * Accessible: role="listitem", checkbox has aria-checked, aria-label describes habit name and status
 *
 * @see Requirements 8.2, 8.3
 */

import { useState, useCallback } from 'react';
import type { Habit } from '../../types';
import { StreakCounter } from './StreakCounter';

export interface HabitItemProps {
  /** The habit data object. */
  habit: Habit;
  /** Whether this habit is completed today. */
  isCompletedToday: boolean;
  /** Called when the user toggles today's completion. */
  onToggle: () => void;
  /** Called when the user clicks the habit (to view detail). */
  onClick: () => void;
}

export function HabitItem({ habit, isCompletedToday, onToggle, onClick }: HabitItemProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleToggle = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      setIsAnimating(true);
      onToggle();
      // Reset animation after 300ms bounce
      setTimeout(() => setIsAnimating(false), 300);
    },
    [onToggle]
  );

  const handleToggleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle(e);
      }
    },
    [handleToggle]
  );

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <div
      role="listitem"
      className={[
        'flex items-center gap-3 px-3 py-3 rounded-md',
        'bg-bg-card hover:bg-hover-bg',
        'transition-all duration-default ease-default',
        'cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
      ].join(' ')}
      onClick={onClick}
      onKeyDown={handleItemKeyDown}
      tabIndex={0}
      aria-label={`${habit.name}, ${isCompletedToday ? 'completed today' : 'not completed today'}, ${habit.currentStreak} day streak`}
    >
      {/* Color dot */}
      <span
        className="shrink-0 rounded-full"
        style={{
          width: '8px',
          height: '8px',
          backgroundColor: habit.color || 'var(--color-primary)',
        }}
        aria-hidden="true"
      />

      {/* Name and streak */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span
          className={[
            'truncate text-sm',
            isCompletedToday ? 'font-bold text-text-heading' : 'font-medium text-text-primary',
          ].join(' ')}
        >
          {habit.name}
        </span>
        <StreakCounter streak={habit.currentStreak} color={habit.color || undefined} />
      </div>

      {/* Completion toggle — 44x44px touch target */}
      <div
        role="checkbox"
        aria-checked={isCompletedToday}
        aria-label={`Mark ${habit.name} as ${isCompletedToday ? 'incomplete' : 'complete'}`}
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleToggleKeyDown}
        className={[
          'shrink-0 flex items-center justify-center',
          'w-[44px] h-[44px] rounded-full',
          'border-2 transition-all duration-default ease-default',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
          'cursor-pointer',
          isCompletedToday
            ? 'bg-primary border-primary text-white'
            : 'border-border bg-transparent hover:border-primary',
          isAnimating ? 'scale-125' : 'scale-100',
        ].join(' ')}
        style={
          isCompletedToday && habit.color
            ? { backgroundColor: habit.color, borderColor: habit.color }
            : undefined
        }
      >
        {isCompletedToday && (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </div>
  );
}

HabitItem.displayName = 'HabitItem';
